// AudioInterviewStage — bidirectional audio stage for HRGPT interview rounds.
// Renders ONLY a state-aware avatar (ai_speaking / listening / thinking) —
// no live transcripts. Final transcript is shown only in the post-interview report.
//
// Usage:
//   <AudioInterviewStage
//      round="preScreening" | "psychoanalytical" | "finalInterview"
//      resumeId={resumeId}
//      onTurn={async (candidateText) => aiNextLine}   // your existing turn handler
//      initialAiLine={openerText}                     // first thing AI says
//      onComplete={(fullTranscript) => ...}           // called when round ends
//   />
//
// Server endpoints used (mounted in server.js):
//   POST /api/public/audio/speak       { text, round, resumeId } -> audio/mpeg
//   POST /api/public/audio/transcribe  (multipart: file, round, resumeId) -> { text }

import React, { useEffect, useRef, useState, useCallback } from "react";

const STATE = { IDLE: "idle", AI_SPEAKING: "ai_speaking", LISTENING: "listening", THINKING: "thinking", DONE: "done" };

// Silence-detection: auto-submit candidate audio after this much continuous silence
const SILENCE_MS = 1800;
const SILENCE_RMS = 0.012;

const API_BASE = (typeof window !== "undefined" && window.__API_BASE__) || ""; // empty → same-origin

export default function AudioInterviewStage({
  round,
  resumeId,
  onTurn,
  initialAiLine,
  onComplete,
  candidateName = "there",
}) {
  const [state, setState] = useState(STATE.IDLE);
  const [error, setError] = useState("");
  const [endedReason, setEndedReason] = useState("");
  const transcriptRef = useRef([]); // [{ role:'ai'|'user', text }]

  const mediaRecorderRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceStartRef = useRef(0);
  const rafRef = useRef(null);
  const chunksRef = useRef([]);
  const aiAudioRef = useRef(null);
  const startTimeRef = useRef(0);

  // ── Speak AI line via ElevenLabs ──────────────────────────────────
  const speakAi = useCallback(async (text) => {
    if (!text) return;
    setState(STATE.AI_SPEAKING);
    transcriptRef.current.push({ role: "ai", text });
    try {
      const res = await fetch(`${API_BASE}/api/public/audio/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, round, resumeId }),
      });
      if (!res.ok) throw new Error("TTS failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      aiAudioRef.current = audio;
      await new Promise((resolve, reject) => {
        audio.onended = () => { URL.revokeObjectURL(url); resolve(); };
        audio.onerror = reject;
        audio.play().catch(reject);
      });
    } catch (e) {
      setError("Audio playback failed: " + e.message);
    }
  }, [round, resumeId]);

  // ── Send candidate audio for STT, then advance turn ───────────────
  const submitCandidateAudio = useCallback(async (blob) => {
    setState(STATE.THINKING);
    try {
      const fd = new FormData();
      fd.append("file", blob, "speech.webm");
      fd.append("round", round);
      if (resumeId) fd.append("resumeId", resumeId);
      const dur = (Date.now() - startTimeRef.current) / 1000;
      fd.append("durationSec", String(dur));

      const r = await fetch(`${API_BASE}/api/public/audio/transcribe`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("STT failed");
      const { text } = await r.json();
      if (!text || !text.trim()) {
        // Silent / nothing transcribed — give them another chance
        await speakAi("Sorry, I didn't catch that. Could you say it again?");
        startListening();
        return;
      }
      transcriptRef.current.push({ role: "user", text });

      const next = await onTurn(text); // your existing turn handler returns next AI line OR { done:true, reason }
      if (next?.done) {
        if (next.aiLine) await speakAi(next.aiLine);
        setEndedReason(next.reason || "completed");
        setState(STATE.DONE);
        onComplete?.(transcriptRef.current.slice());
        return;
      }
      const aiLine = typeof next === "string" ? next : next?.aiLine;
      if (!aiLine) throw new Error("Turn handler returned no next line");
      await speakAi(aiLine);
      startListening();
    } catch (e) {
      setError(e.message);
      setState(STATE.IDLE);
    }
  }, [round, resumeId, onTurn, speakAi, onComplete]);

  // ── Listening + silence detection ─────────────────────────────────
  const startListening = useCallback(async () => {
    try {
      setState(STATE.LISTENING);
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        if (audioCtxRef.current) audioCtxRef.current.close().catch(()=>{});
        cancelAnimationFrame(rafRef.current);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        submitCandidateAudio(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      startTimeRef.current = Date.now();

      // RMS-based silence detector
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.fftSize);
      silenceStartRef.current = 0;
      let spokeAtLeastOnce = false;

      const tick = () => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / data.length);
        const now = Date.now();
        if (rms > SILENCE_RMS) {
          spokeAtLeastOnce = true;
          silenceStartRef.current = now;
        } else if (spokeAtLeastOnce && silenceStartRef.current && now - silenceStartRef.current > SILENCE_MS) {
          // auto-stop
          if (mr.state === "recording") mr.stop();
          return;
        } else if (!silenceStartRef.current) {
          silenceStartRef.current = now;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) {
      setError("Mic access denied or unavailable. " + e.message);
      setState(STATE.IDLE);
    }
  }, [submitCandidateAudio]);

  // ── Kick off: AI says opener, then we listen ──────────────────────
  const begin = useCallback(async () => {
    setError("");
    const opener = (initialAiLine || `Hello ${candidateName}, let's get started.`).trim();
    await speakAi(opener);
    startListening();
  }, [initialAiLine, candidateName, speakAi, startListening]);

  // Cleanup
  useEffect(() => () => {
    try { aiAudioRef.current?.pause(); } catch {}
    try { mediaRecorderRef.current?.stop(); } catch {}
    cancelAnimationFrame(rafRef.current);
  }, []);

  // ── UI ────────────────────────────────────────────────────────────
  const stateText = {
    [STATE.IDLE]:        "Tap Start to begin",
    [STATE.AI_SPEAKING]: "Interviewer is speaking…",
    [STATE.LISTENING]:   "Listening — speak now",
    [STATE.THINKING]:    "Thinking…",
    [STATE.DONE]:        "Interview complete",
  }[state];

  const ringClass =
    state === STATE.AI_SPEAKING ? "ring-blue-500 animate-pulse" :
    state === STATE.LISTENING   ? "ring-emerald-500 animate-pulse" :
    state === STATE.THINKING    ? "ring-amber-500" :
                                   "ring-slate-300";

  return (
    <div className="flex flex-col items-center justify-center p-8 min-h-[60vh] bg-gradient-to-b from-slate-50 to-white rounded-2xl">
      <div className={`w-44 h-44 rounded-full bg-white shadow-xl ring-8 ${ringClass} flex items-center justify-center transition-all`}>
        <div className="text-5xl">{state === STATE.LISTENING ? "🎙️" : state === STATE.AI_SPEAKING ? "🗣️" : state === STATE.THINKING ? "🧠" : "👋"}</div>
      </div>
      <div className="mt-6 text-lg font-medium text-slate-700">{stateText}</div>
      {error && <div className="mt-3 text-sm text-rose-600">{error}</div>}

      {state === STATE.IDLE && (
        <button
          onClick={begin}
          className="mt-6 px-6 py-3 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 shadow"
        >
          Start interview
        </button>
      )}

      {state === STATE.LISTENING && (
        <button
          onClick={() => { try { mediaRecorderRef.current?.stop(); } catch {} }}
          className="mt-6 px-4 py-2 rounded-full bg-slate-200 text-slate-700 hover:bg-slate-300 text-sm"
        >
          I'm done speaking
        </button>
      )}

      {state === STATE.DONE && (
        <div className="mt-4 text-sm text-slate-500">{endedReason || "Thanks — your responses have been recorded."}</div>
      )}
    </div>
  );
}
